import { AlertCircle, CircleCheck, LoaderCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useWorkScope, type ProblemOccurrence } from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../ui/button'
import { ComponentChip } from '../primitives/ComponentChip'
import { StatusPill } from '../primitives/StatusPill'
import { formatCompactLocalTime } from '../../lib/time'
import { cn } from '../../lib/utils'
import { useOccurrenceStatusAction } from './OccurrenceStatusAction'
import { workMutationError } from './work-model'

/**
 * One problem in a list: its name, the equipment it affects, when it opened
 * and Close / Reopen. Clicking it opens the problem's own page. A draft is not
 * published yet, so it has no Close.
 */
export function ProblemSummaryRow({
  occurrence,
  href,
  draft = false
}: {
  occurrence: ProblemOccurrence
  href: string
  /** Recorded by a running investigation but not published yet. */
  draft?: boolean
}) {
  const closed = occurrence.status === 'closed'
  const affected = occurrence.affected_entities.filter((item) => item.type !== 'Line')
  const name = occurrence.problem_name || 'New problem'
  return (
    <li className="relative transition-colors duration-150 hover:bg-bg-sunk/45">
      <div
        className={cn(
          'grid grid-cols-[1rem_minmax(0,1fr)_max-content_max-content] items-center gap-x-2.5 px-3 py-2.5',
          affected.length > 0 && 'gap-y-1'
        )}
      >
        <span className="row-start-1" aria-hidden>
          {draft ? (
            <LoaderCircle
              size={15}
              className="animate-spin text-brand motion-reduce:animate-none"
            />
          ) : closed ? (
            <CircleCheck size={15} className="text-st-run" />
          ) : (
            <AlertCircle size={15} className="text-st-down" />
          )}
        </span>
        {/* The link covers the whole row; the chips and the button sit above it. */}
        <Link
          to={href}
          className="col-start-2 row-start-1 min-w-0 break-words text-14 font-medium leading-5 text-ink no-underline outline-none after:absolute after:inset-0 focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-brand"
        >
          {name}
        </Link>
        <span className="col-start-3 row-start-1 flex items-center gap-2">
          {draft && (
            <StatusPill status="info" withDot={false}>
              Investigating
            </StatusPill>
          )}
          <time
            dateTime={occurrence.opened_at}
            title={`Opened ${new Date(occurrence.opened_at).toLocaleString()}`}
            className="whitespace-nowrap text-12 leading-5 text-ink-3"
          >
            {formatCompactLocalTime(occurrence.opened_at)}
          </time>
        </span>
        <span className="col-start-4 row-start-1 empty:hidden">
          {!draft && <StatusAction occurrence={occurrence} />}
        </span>
        {affected.length > 0 && (
          <div
            aria-label="Affected equipment"
            className="relative z-10 col-start-2 row-start-2 flex w-fit max-w-full flex-wrap items-center gap-1.5"
          >
            <span className="mr-0.5 text-12 text-ink-3">Affects</span>
            {affected.map((item) => (
              <ComponentChip
                key={item.id}
                component={{
                  id: item.id,
                  name: item.name,
                  scope: item.type === 'Tag' ? 'tag' : 'equipment'
                }}
                hideScope
                className="max-w-full rounded border-line/70 bg-bg-sunk/50 px-1.5 py-px text-12 font-normal text-ink-2 [&>span]:truncate"
              />
            ))}
          </div>
        )}
      </div>
    </li>
  )
}

function StatusAction({ occurrence }: { occurrence: ProblemOccurrence }) {
  const status = useOccurrenceStatusAction(useWorkScope(), occurrence)
  if (!status.canAct) return null
  return (
    <span className="relative z-10 flex flex-col items-end gap-1">
      <Button
        size="xs"
        variant="outline"
        className={cn(
          'h-7 rounded px-2 text-12 font-medium shadow-none',
          status.closed
            ? 'border-brand/20 bg-brand/5 text-brand hover:border-brand/40 hover:bg-brand/10 hover:text-brand dark:text-sky-300'
            : 'border-st-run/20 bg-st-run-bg/40 text-st-run hover:border-st-run/40 hover:bg-st-run-bg hover:text-st-run dark:border-emerald-400/20 dark:bg-emerald-400/5 dark:text-emerald-300 dark:hover:bg-emerald-400/10 dark:hover:text-emerald-300'
        )}
        disabled={status.acting}
        onClick={status.act}
      >
        {status.closed ? 'Reopen' : 'Close'}
      </Button>
      {status.error ? (
        <span role="alert" className="max-w-56 text-right text-12 text-st-down">
          {workMutationError(status.error)}
        </span>
      ) : null}
    </span>
  )
}
